import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Data, Company, Invitation, Rib, Carnet, ReceivedCheck, Check } from 'src/types/data';
// import { HOST_API } from 'src/config-global';
import {
  updateCompanyInList,
  filterCompaniesByIds,
  removeCompanyFromList,
  addInvitation as addInvitationUtil,
  updateInvitation as updateInvitationUtil,
  removeInvitation as removeInvitationUtil,
} from 'src/utils/dataUtils';
import { addRib, updateRib, removeRib } from 'src/utils/ribUtils';
import { addCarnet, updateCarnet, removeCarnet } from 'src/utils/carnetUtils';
import { addReceivedCheck, updateReceivedCheck, removeReceivedCheck } from 'src/utils/receivedCheckUtils';
import { addCheckToCarnet, updateCheckInCarnet, removeCheckFromCarnet} from 'src/utils/checkUtils';

// Initial state
interface DataState {
  data: Data | null;
  selectedCompanyIds: number[];
  filteredData: Company[];
  invitations: Invitation[];
  loading: boolean;
  error: string | null;
}
const initialState: DataState = {
  data: null,
  selectedCompanyIds: [],
  filteredData: [],
  invitations: [],
  loading: false,
  error: null,
};

// Async thunk to fetch data
// export const fetchData = createAsyncThunk<Data, void, { rejectValue: string }>(
//   'data/fetchData',
//   async (_, { rejectWithValue }) => {
//     try {
//       const accessToken = localStorage.getItem('lbankaAccessToken');
//       const headers = {
//         'Content-Type': 'application/json',
//         ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
//       };

//       const response = await fetch(`${HOST_API}/data`, { headers });
//       if (!response.ok) {
//         throw new Error('Failed to fetch data');
//       }

//       const data: Data = await response.json();
//       return data;
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );
export const fetchData = createAsyncThunk<Data, Data, { rejectValue: string }>(
  'data/fetchData',
  async (data, { rejectWithValue }) => {
    try {
      return data; // Simply return the data passed as an argument
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Data slice
const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    setSelectedCompanyIds(state, action: PayloadAction<number[]>) {
      state.selectedCompanyIds = action.payload;
      state.filteredData = state.data
        ? filterCompaniesByIds(state.data.companies, action.payload)
        : [];
    },
    updateFilteredData(state, action: PayloadAction<Company[]>) {
      if (state.data) {
        const selectedIds = state.selectedCompanyIds || [];
        state.filteredData = action.payload.filter((company) =>
          selectedIds.includes(company.id)
        );
      }
    },
    clearData(state) {
      state.data = null;
      state.selectedCompanyIds = [];
      state.filteredData = [];
      state.invitations = [];
    },
    addCompany(state, action: PayloadAction<Company>) {
      if (state.data) {
        state.data.companies.push(action.payload);
        state.selectedCompanyIds.push(action.payload.id);
        state.filteredData = filterCompaniesByIds(
          state.data.companies,
          state.selectedCompanyIds
        );
      }
    },
    updateCompany(state, action: PayloadAction<Partial<Company> & { id: number }>) {
      if (state.data) {
        state.data.companies = updateCompanyInList(
          state.data.companies,
          action.payload
        );
        state.filteredData = filterCompaniesByIds(
          state.data.companies,
          state.selectedCompanyIds
        );
      }
    },    
    removeCompany(state, action: PayloadAction<number>) {
      if (state.data) {
        state.data.companies = removeCompanyFromList(
          state.data.companies,
          action.payload
        );
        state.selectedCompanyIds = state.selectedCompanyIds.filter(
          (id) => id !== action.payload
        );
        state.filteredData = filterCompaniesByIds(
          state.data.companies,
          state.selectedCompanyIds
        );
      }
    },
    addInvitation(state, action: PayloadAction<Invitation>) {
      if (state.data) {
        state.invitations = addInvitationUtil(state.invitations, action.payload);
      }
    },
    updateInvitation(state, action: PayloadAction<Invitation>) {
      if (state.data) {
        state.invitations = updateInvitationUtil(state.invitations, action.payload);
      }
    },
    removeInvitation(state, action: PayloadAction<number>) {
      if (state.data) {
        state.invitations = removeInvitationUtil(state.invitations, action.payload);
      }
    },
    addRibToCompany(state, action: PayloadAction<{ companyId: number; rib: Rib }>) {
      if (state.data) {
        state.data.companies = addRib(state.data.companies, action.payload.companyId, action.payload.rib);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    updateRibInCompany(state, action: PayloadAction<{ companyId: number; rib: Rib }>) {
      if (state.data) {
        state.data.companies = updateRib(state.data.companies, action.payload.companyId, action.payload.rib);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    removeRibFromCompany(state, action: PayloadAction<{ companyId: number; ribId: number }>) {
      if (state.data) {
        state.data.companies = removeRib(state.data.companies, action.payload.companyId, action.payload.ribId);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    addCarnetToRib(state, action: PayloadAction<{ companyId: number; ribId: number; carnet: Carnet }>) {
      if (state.data) {
        state.data.companies = addCarnet(state.data.companies, action.payload.companyId, action.payload.ribId, action.payload.carnet);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    updateCarnetInRib(state, action: PayloadAction<{ companyId: number; ribId: number; carnet: Carnet }>) {
      if (state.data) {
        state.data.companies = updateCarnet(state.data.companies, action.payload.companyId, action.payload.ribId, action.payload.carnet);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    removeCarnetFromRib(state, action: PayloadAction<{ companyId: number; ribId: number; carnetId: number }>) {
      if (state.data) {
        state.data.companies = removeCarnet(state.data.companies, action.payload.companyId, action.payload.ribId, action.payload.carnetId);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    addReceivedCheckToRib(state, action: PayloadAction<{ companyId: number; ribId: number; receivedCheck: ReceivedCheck }>) {
      if (state.data) {
        state.data.companies = addReceivedCheck(state.data.companies, action.payload.companyId, action.payload.ribId, action.payload.receivedCheck);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    updateReceivedCheckInRib(
      state,
      action: PayloadAction<{ companyId: number; ribId: number; receivedCheck: Partial<ReceivedCheck> }>
    ) {
      if (state.data) {
        state.data.companies = updateReceivedCheck(
          state.data.companies,
          action.payload.companyId,
          action.payload.ribId,
          action.payload.receivedCheck
        );
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    removeReceivedCheckFromRib(state, action: PayloadAction<{ companyId: number; ribId: number; checkId: number }>) {
      if (state.data) {
        state.data.companies = removeReceivedCheck(state.data.companies, action.payload.companyId, action.payload.ribId, action.payload.checkId);
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    addIssuedCheck(
      state,
      action: PayloadAction<{ companyId: number; ribId: number; carnetId: number; check: Check }>
    ) {
      if (state.data) {
        state.data.companies = addCheckToCarnet(
          state.data.companies,
          action.payload.companyId,
          action.payload.ribId,
          action.payload.carnetId,
          action.payload.check
        );
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    updateIssuedCheck(
      state,
      action: PayloadAction<{ companyId: number; ribId: number; carnetId: number; check: Partial<Check> }>
    ) {
      if (state.data) {
        state.data.companies = updateCheckInCarnet(
          state.data.companies,
          action.payload.companyId,
          action.payload.ribId,
          action.payload.carnetId,
          action.payload.check
        );
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
    removeIssuedCheck(
      state,
      action: PayloadAction<{ companyId: number; ribId: number; carnetId: number; checkId: number }>
    ) {
      if (state.data) {
        state.data.companies = removeCheckFromCarnet(
          state.data.companies,
          action.payload.companyId,
          action.payload.ribId,
          action.payload.carnetId,
          action.payload.checkId
        );
        state.filteredData = filterCompaniesByIds(state.data.companies, state.selectedCompanyIds);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.invitations = action.payload.invitations;

        const allCompanyIds = action.payload.companies.map((company) => company.id);
        state.selectedCompanyIds = allCompanyIds;
        state.filteredData = action.payload.companies;
      })
      .addCase(fetchData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch data';
      });
  },
});

// Export actions and reducer
export const {
  setSelectedCompanyIds,
  updateFilteredData,
  // clearData,
  addCompany,
  updateCompany,
  removeCompany,
  addInvitation,
  removeInvitation,
  // updateInvitation,
  addRibToCompany,
  updateRibInCompany,
  removeRibFromCompany,
  addCarnetToRib,
  updateCarnetInRib,
  removeCarnetFromRib,
  addReceivedCheckToRib,
  updateReceivedCheckInRib,
  // removeReceivedCheckFromRib,
  addIssuedCheck,
  updateIssuedCheck,
  // removeIssuedCheck,
} = dataSlice.actions;
export default dataSlice.reducer;
